/**
 * 需求来源：docs/旅游落地页/AI提示词.md 第1次提交（任务单 DEV-120352）；视觉基准 docs/旅游落地页/cj商旅-01.html。
 * 注意事项：
 * 1. /cj-business-travel 为纯展示型 React SSR 落地页（CJ 商旅），按原型 1:1 还原 8 大区块：
 *    HERO(banner) / ABOUT / TRIP(Route Planner + Six Features) / EXPERIENCES(四态 Editorial selector) /
 *    SERVICES(三卡 + Destinations) / PAYMENT(价格 + CJ Points + Core Values + Includes) /
 *    CITIES(City Explorer 八城) / CTA(Plan Your Journey)；
 *    另有第 9 块 CITY_VIDEOS(中国城市竖版视频，DEV-126262 后加)，源码由提出方另行提供、非首版原型内容。
 * 2. 所有可见文案统一走 i18next.t('cjbusinesstravel-{区块}-{描述}', '英文源')，**key 全小写**（翻译中心上报要求），
 *    英文源即原型原文（保留 U+2019 弯引号等原始字符）；词库落盘 public/lang/local/cjBusinessTravel.json，
 *    pageId=cjBusinessTravel（见 public/lang/router.json）。
 * 3. 同构安全：CommonJS，禁止 import/export 与 fs/path；i18next 取词器复用 mcpLanding/i18nText，
 *    SSR/未初始化时回退英文源。
 * 4. 本文件仅维护展示层常量，不承载跳转/状态逻辑；外链（CJEC 票务、WhatsApp、积分商城）为固定值不做翻译。
 * 5. 城市数据体量大，已按领域拆到同目录 cityData.js；图片静态映射见 images.js。
 */
'use strict';

var createImg = require('./images').createImg;
var getCities = require('./cityData').getCities;
var imgUrl = require('./images').url;

// 原型固定外链（第六节 Open Questions 中的两处占位符 YOUR_PAYMENT_PAGE_URL / YOUR_CJ_POINTS_PAGE_URL
// 在原型 HTML 中已由作者替换为下列真实地址，此处照原型取值，不再保留占位符）
var LINK_PAYMENT = 'https://cjec.cjdropshipping.com/cjec-tickets/#cjec-dual-packages';
var LINK_WHATSAPP = 'https://wa.me/8618658117813';
var LINK_POINTS = '/my.html#/points-mall';

/**
 * 取本页全部展示常量（每次调用重新取词，语言切换后文案随之更新）。
 * @returns {Object} 各区块常量：HERO/ABOUT/TRIP/EXPERIENCES/CITY_VIDEOS/SERVICES/PAYMENT/CITIES/CTA
 */
function getConstants(cdnHost) {
  var i18next = require('./../mcpLanding/i18nText').getI18next();
  var img = createImg(cdnHost);
  var __ready = !!(i18next && i18next.isInitialized);

  /**
   * 变量插值兜底：把文案里的 {{var}} 手动替换掉，保证任何时刻返回值都不残留 {{}} 占位符
   * （单测 CASE-C2 与 CASE-S2 对此有断言）。
   *
   * 需求来源：AI提示词.md 第5次提交（第12次补充，DEV-120352）—— 翻译上报漏了本页两个带插值的词条。
   * 【为什么取词必须写在调用处、且 key 必须是字面量】
   * 翻译上报工具 @cckj/i18n-util 的 extract 是**文本扫描**，逐个词库 key 去代码里找
   *   new RegExp(`i18next\\.t\\(\\s*['"\`]<key>['"\`]`)
   * 也就是要求 key 以**字面量**紧跟在 `i18next.t(` 之后。此前这两个词条封在
   * `dyn(key, def, vars)` 里由 `i18next.t(key, ...)` 取词，key 是**变量**，扫描匹配不到，
   * 于是 429 个词条只上报了 427 个 —— 是真实漏报，不是工具误差。
   * 故本函数只保留插值兜底，取词一律在调用处以 `i18next.t('<字面量 key>', ...)` 写出。
   * 该约束由单测 CASE-L4 逐 key 用同一条正则复核，防止再退回变量取词。
   *
   * @param {string} raw i18next 取到的文案（未初始化时传空串）
   * @param {string} def 英文源（含 {{var}} 占位）
   * @param {Object} vars 插值变量
   */
  function fillVars(raw, def, vars) {
    var s = typeof raw === 'string' && raw ? raw : def;
    if (vars) {
      Object.keys(vars).forEach(function (k) { s = s.split('{{' + k + '}}').join(vars[k]); });
    }
    return s;
  }

  // ============================== 区块一：HERO banner ==============================
  // 原型 .cjbt-v4：左文案 + 右双列图片轨道（上下反向无限滚动）
  var HERO = {
    eyebrow: i18next.t('cjbusinesstravel-hero-eyebrow', 'CJ Business Travel'),
    titleLead: i18next.t('cjbusinesstravel-hero-title-lead', 'Discover the'),
    titleAccent: i18next.t('cjbusinesstravel-hero-title-accent', 'Real China'),
    titleBottom: i18next.t('cjbusinesstravel-hero-title-bottom', 'Beyond the Ordinary'),
    description: i18next.t('cjbusinesstravel-hero-description', 'Explore vibrant cities, Chinese culture, authentic local food, and unforgettable experiences, with optional factory visits, supply chain discovery added to your journey.'),
    features: [
      i18next.t('cjbusinesstravel-hero-feature-1', 'City Exploration'),
      i18next.t('cjbusinesstravel-hero-feature-2', 'Culture & Food'),
      i18next.t('cjbusinesstravel-hero-feature-3', 'Customized Journey'),
      i18next.t('cjbusinesstravel-hero-feature-4', 'Factory Visits'),
      i18next.t('cjbusinesstravel-hero-feature-5', 'Local Life Experience'),
      i18next.t('cjbusinesstravel-hero-feature-6', 'Private Travel Service'),
    ],
    primaryCta: i18next.t('cjbusinesstravel-hero-cta-primary', 'Plan My Journey'),
    // 第14次补充：两个主 CTA 改跳「价格与支付」区块（原先跳 #cjbt-plan-your-journey）
    primaryCtaHref: '#cjbt-package-payment',
    secondaryCta: i18next.t('cjbusinesstravel-hero-cta-secondary', 'Explore Experiences'),
    secondaryCtaHref: '#featured-china-experiences',
    support: i18next.t('cjbusinesstravel-hero-support', 'Personalized China journeys supported by a trusted local team.'),
    // 第2次补充：Hero 区块 CSS 背景大图随资源仓发布，CSS 内取不到 cdnHost，
    // 故由组件注入 --cjbt-hero-bg（值含 url(...)），见 index.less 文件头说明。
    backgroundCss: 'url(' + imgUrl(cdnHost, 'cjBusinessTravel30.jpg') + ')',
    // 两列反向滚动轨道：direction/speed 对应原型 data-cjbt-direction / data-cjbt-speed
    columns: [
      {
        direction: 'up',
        speed: 30,
        items: [
          // 第15次补充需求1：原型自带错字 Gangzhou（第1次提交按「原型原文保真」带进来的），业务方指出应为 Guangzhou。
          // 【为什么换 key 而不是改原 key 的值】cjbusinesstravel-hero-card-1-city 已上报翻译中心并译完
          // 各语种，译文按 key 存 —— 只改本地英文源，非英文站仍会取到按 "Gangzhou" 译出的旧译文，等于没修。
          // 故改为复用**现成且已译好**的 cjbusinesstravel-city-guangzhou-name（City Explorer 的
          // Guangzhou 城市名，值本就是 Guangzhou），原 hero-card-1-city 词条随之删除（CASE-C7 锁住这一点）。
          mergeCaption(img('cjBusinessTravel01.jpg'), i18next.t('cjbusinesstravel-city-guangzhou-name', 'Guangzhou'), i18next.t('cjbusinesstravel-hero-card-1-alt', 'Modern Chinese city'), 'eager'),
          mergeCaption(img('cjBusinessTravel02.jpg'), i18next.t('cjbusinesstravel-hero-card-2-city', 'Shenzhen'), i18next.t('cjbusinesstravel-hero-card-2-alt', 'Traditional Chinese culture'), 'eager'),
          // 第15次补充需求6：03 → 68（业务方给的「图片2」，大熊猫）。
          // 本条只换图，city/alt 文案一律不动 —— 开发 07-30 明确「改图片的地方不要动文案，
          // 我没有要求你改的文案就不要改」。
          mergeCaption(img('cjBusinessTravel68.jpg'), i18next.t('cjbusinesstravel-hero-card-3-city', 'Chengdu'), i18next.t('cjbusinesstravel-hero-card-3-alt', 'Chinese city and travel experience'), 'lazy'),
          mergeCaption(img('cjBusinessTravel04.jpg'), i18next.t('cjbusinesstravel-hero-card-4-city', 'Chongqing'), i18next.t('cjbusinesstravel-hero-card-4-alt', 'China cultural travel experience'), 'lazy'),
        ],
      },
      {
        direction: 'down',
        speed: 27,
        items: [
          // 第15次补充需求6：05 → 67（业务方给的「图片1」，兵马俑）。同上，只换图、不动 city/alt 文案。
          mergeCaption(img('cjBusinessTravel67.jpg'), i18next.t('cjbusinesstravel-hero-card-5-city', 'Xi’an'), i18next.t('cjbusinesstravel-hero-card-5-alt', 'Chinese food and local life'), 'eager'),
          mergeCaption(img('cjBusinessTravel06.jpg'), i18next.t('cjbusinesstravel-hero-card-6-city', 'Hangzhou'), i18next.t('cjbusinesstravel-hero-card-6-alt', 'Business visit in China'), 'eager'),
          mergeCaption(img('cjBusinessTravel07.jpg'), i18next.t('cjbusinesstravel-hero-card-7-city', 'Shanghai'), i18next.t('cjbusinesstravel-hero-card-7-alt', 'Factory and supplier visit'), 'lazy'),
          mergeCaption(img('cjBusinessTravel08.jpg'), i18next.t('cjbusinesstravel-hero-card-8-city', 'Beijing'), i18next.t('cjbusinesstravel-hero-card-8-alt', 'Beijing'), 'lazy'),
          mergeCaption(img('cjBusinessTravel09.jpg'), i18next.t('cjbusinesstravel-hero-card-9-city', 'Zhangjiajie'), i18next.t('cjbusinesstravel-hero-card-9-alt', 'Zhangjiajie'), 'lazy'),
        ],
      },
    ],
  };

  // ============================== 区块二：About ==============================
  // 原型 .cjbt-about-v2：左文案（含 statement 与三条 values）+ 右主图/小图/图注
  var ABOUT = {
    eyebrow: i18next.t('cjbusinesstravel-about-eyebrow', 'About CJ Business Travel'),
    title: i18next.t('cjbusinesstravel-about-title', 'Discover the Beauty of Chinese Culture'),
    titleAccent: i18next.t('cjbusinesstravel-about-title-accent', 'with CJ'),
    // 原型此段首词 CJ Business Travel 加粗，渲染侧拆 strong + 余文
    descriptionStrong: i18next.t('cjbusinesstravel-about-description-strong', 'CJ Business Travel'),
    description: i18next.t('cjbusinesstravel-about-description', 'creates personalized journeys for international visitors who want to experience the real China. Explore vibrant cities, traditional culture, authentic local cuisine, famous landmarks, and everyday life, with optional factory visits and access to supply chain resources.'),
    statementLabel: i18next.t('cjbusinesstravel-about-statement-label', 'More Than a Travel Service'),
    statementStrong: i18next.t('cjbusinesstravel-about-statement-strong', 'We do more than arrange transportation and hotels.'),
    statementText: i18next.t('cjbusinesstravel-about-statement-text', 'Our local team helps international visitors understand China more easily, travel more confidently, and experience the country more deeply.'),
    values: [
      { number: '01', text: i18next.t('cjbusinesstravel-about-value-1', 'Personalized Journeys') },
      { number: '02', text: i18next.t('cjbusinesstravel-about-value-2', 'Authentic Local Experiences') },
      { number: '03', text: i18next.t('cjbusinesstravel-about-value-3', 'Optional Business Access') },
    ],
    mainImage: img('cjBusinessTravel10.jpg').src,
    mainImageName: img('cjBusinessTravel10.jpg').imageName,
    mainImageAlt: i18next.t('cjbusinesstravel-about-main-image-alt', 'International visitors exploring China'),
    smallImage: img('cjBusinessTravel11.jpg').src,
    smallImageName: img('cjBusinessTravel11.jpg').imageName,
    smallImageAlt: i18next.t('cjbusinesstravel-about-small-image-alt', 'Traditional Chinese culture and local experience'),
    captionTitle: i18next.t('cjbusinesstravel-about-caption-title', 'See China Differently'),
    captionText: i18next.t('cjbusinesstravel-about-caption-text', 'Culture · Cities · Food · Local Life'),
    captionMark: i18next.t('cjbusinesstravel-about-caption-mark', 'CJ'),
  };

  // ============================== 区块三：Route Planner + Six Features ==============================
  // 原型 .cjbt-trip-center
  var TRIP = {
    eyebrow: i18next.t('cjbusinesstravel-trip-eyebrow', 'Customized China Journey'),
    titleLead: i18next.t('cjbusinesstravel-trip-title-lead', 'Explore China Through a Journey'),
    titleEm: i18next.t('cjbusinesstravel-trip-title-em', 'Designed for You'),
    descriptionLead: i18next.t('cjbusinesstravel-trip-description-lead', 'We create a personalized China itinerary based on your'),
    descriptionStrong: i18next.t('cjbusinesstravel-trip-description-strong', 'travel dates, budget, interests, and preferred travel style'),
    descriptionTail: i18next.t('cjbusinesstravel-trip-description-tail', '. Choose one city for a deeper experience or combine several destinations into a customized multi-city journey.'),
    plannerTitle: i18next.t('cjbusinesstravel-trip-planner-title', 'Your Personalized China Route'),
    plannerSubtitle: i18next.t('cjbusinesstravel-trip-planner-subtitle', 'Flexible cities, flexible days, and flexible experiences'),
    routePoints: [
      { node: '01', title: i18next.t('cjbusinesstravel-trip-route-1-title', 'Choose Your Dates'), text: i18next.t('cjbusinesstravel-trip-route-1-text', 'Travel at a time that works for you.') },
      { node: '02', title: i18next.t('cjbusinesstravel-trip-route-2-title', 'Select Your Cities'), text: i18next.t('cjbusinesstravel-trip-route-2-text', 'Choose one city or multiple destinations.') },
      { node: '03', title: i18next.t('cjbusinesstravel-trip-route-3-title', 'Add Experiences'), text: i18next.t('cjbusinesstravel-trip-route-3-text', 'Combine culture, food, nature, and local life.') },
      { node: '04', title: i18next.t('cjbusinesstravel-trip-route-4-title', 'Set Your Travel Pace'), text: i18next.t('cjbusinesstravel-trip-route-4-text', 'Choose how long you stay in each city.') },
      { node: '05', title: i18next.t('cjbusinesstravel-trip-route-5-title', 'Confirm Your Journey'), text: i18next.t('cjbusinesstravel-trip-route-5-text', 'Receive and confirm your personalized itinerary.') },
    ],
    tags: [
      i18next.t('cjbusinesstravel-trip-tag-1', 'Single-City Deep Experience'),
      i18next.t('cjbusinesstravel-trip-tag-2', 'Customized Multi-City Journey'),
      i18next.t('cjbusinesstravel-trip-tag-3', 'Flexible Travel Duration'),
      i18next.t('cjbusinesstravel-trip-tag-4', 'Optional Business Activities'),
    ],
    features: [
      { number: '01', title: i18next.t('cjbusinesstravel-trip-feature-1-title', 'Designed Around Your Preferences'), text: i18next.t('cjbusinesstravel-trip-feature-1-text', 'Your itinerary is planned according to your travel dates, available budget, personal interests, and preferred pace.') },
      { number: '02', title: i18next.t('cjbusinesstravel-trip-feature-2-title', 'Single-City Deep Experience'), text: i18next.t('cjbusinesstravel-trip-feature-2-text', 'Choose one destination and explore its culture, food, landmarks, neighborhoods, and local lifestyle in greater depth.') },
      { number: '03', title: i18next.t('cjbusinesstravel-trip-feature-3-title', 'Customized Multi-City Routes'), text: i18next.t('cjbusinesstravel-trip-feature-3-text', 'Combine several Chinese cities into one coordinated journey based on distance, transportation, and your travel interests.') },
      { number: '04', title: i18next.t('cjbusinesstravel-trip-feature-4-title', 'Flexible Travel Duration'), text: i18next.t('cjbusinesstravel-trip-feature-4-text', 'Adjust the total number of travel days and decide how long you would like to stay in each destination.') },
      { number: '05', title: i18next.t('cjbusinesstravel-trip-feature-5-title', 'Travel and Business Combined'), text: i18next.t('cjbusinesstravel-trip-feature-5-text', 'Combine city sightseeing and cultural experiences with optional business meetings and industrial visits.') },
      { number: '06', title: i18next.t('cjbusinesstravel-trip-feature-6-title', 'More Chinese Cities Available'), text: i18next.t('cjbusinesstravel-trip-feature-6-text', 'Additional destinations can be added according to your travel goals, schedule, and special requirements.') },
    ],
  };

  // ============================== 区块四：Featured China Experiences（四态切换） ==============================
  // 原型 .cjbt-xp-luxe：左侧 tab 竖列（带自动播放进度条）+ 右侧舞台面板（视差）
  var EXPERIENCES = {
    eyebrow: i18next.t('cjbusinesstravel-xp-eyebrow', 'Featured China Experiences'),
    titleLead: i18next.t('cjbusinesstravel-xp-title-lead', 'Experience the'),
    titleEm: i18next.t('cjbusinesstravel-xp-title-em', 'Real China'),
    intro: i18next.t('cjbusinesstravel-xp-intro', 'Explore China through its history, modern cities, regional cuisine, and everyday local life. Select an experience to preview what can be included in your personalized journey.'),
    railLabel: i18next.t('cjbusinesstravel-xp-rail-label', 'Featured China experiences'),
    subheading: i18next.t('cjbusinesstravel-xp-subheading', 'Featured experiences may include:'),
    tabs: [
      {
        id: 'history',
        number: '01',
        image: img('cjBusinessTravel12.jpg').src,
        imageName: img('cjBusinessTravel12.jpg').imageName,
        name: i18next.t('cjbusinesstravel-xp-1-name', 'Historic China'),
        tagline: i18next.t('cjbusinesstravel-xp-1-tagline', 'History & heritage'),
        mediaLabel: i18next.t('cjbusinesstravel-xp-1-media-label', 'History & Heritage'),
        mediaTitle: i18next.t('cjbusinesstravel-xp-1-media-title', 'Step into China’s Living History'),
        mediaAlt: i18next.t('cjbusinesstravel-xp-1-media-alt', 'Historic China, traditional architecture and cultural landmarks'),
        category: i18next.t('cjbusinesstravel-xp-1-category', 'Historic China'),
        title: i18next.t('cjbusinesstravel-xp-1-title', 'Discover China’s Cultural Heritage'),
        text: i18next.t('cjbusinesstravel-xp-1-text', 'Explore ancient cities, imperial palaces, temples, traditional gardens, historic neighborhoods, and villages that preserve China’s rich history and culture.'),
        chips: [
          i18next.t('cjbusinesstravel-xp-1-chip-1', 'Forbidden City'),
          i18next.t('cjbusinesstravel-xp-1-chip-2', 'Great Wall'),
          i18next.t('cjbusinesstravel-xp-1-chip-3', 'Xi’an Ancient City'),
          i18next.t('cjbusinesstravel-xp-1-chip-4', 'Terracotta Army'),
          i18next.t('cjbusinesstravel-xp-1-chip-5', 'Suzhou Gardens'),
          i18next.t('cjbusinesstravel-xp-1-chip-6', 'Hangzhou West Lake'),
        ],
      },
      {
        id: 'modern',
        number: '02',
        // 第15次补充需求6：13 → 69（业务方给的「图片3」，杭州钱江新城夜景）。
        // 该图仍是现代城市天际线，mediaAlt 'Modern Chinese city skyline and urban lifestyle' 与新图吻合，不动。
        image: img('cjBusinessTravel69.jpg').src,
        imageName: img('cjBusinessTravel69.jpg').imageName,
        name: i18next.t('cjbusinesstravel-xp-2-name', 'Modern China'),
        tagline: i18next.t('cjbusinesstravel-xp-2-tagline', 'Cities & innovation'),
        mediaLabel: i18next.t('cjbusinesstravel-xp-2-media-label', 'Modern Cities'),
        mediaTitle: i18next.t('cjbusinesstravel-xp-2-media-title', 'Discover the Energy of Modern China'),
        mediaAlt: i18next.t('cjbusinesstravel-xp-2-media-alt', 'Modern Chinese city skyline and urban lifestyle'),
        category: i18next.t('cjbusinesstravel-xp-2-category', 'Modern China'),
        title: i18next.t('cjbusinesstravel-xp-2-title', 'Experience China’s Modern Energy'),
        text: i18next.t('cjbusinesstravel-xp-2-text', 'Discover modern cities, technology, digital development, innovative industries, striking skylines, and the distinctive rhythm of contemporary urban life.'),
        chips: [
          i18next.t('cjbusinesstravel-xp-2-chip-1', 'Shanghai Skyline'),
          i18next.t('cjbusinesstravel-xp-2-chip-2', 'Shenzhen Innovation'),
          i18next.t('cjbusinesstravel-xp-2-chip-3', 'Guangzhou Night Views'),
          i18next.t('cjbusinesstravel-xp-2-chip-4', 'Hangzhou Digital Economy'),
          i18next.t('cjbusinesstravel-xp-2-chip-5', 'Chongqing Cityscape'),
          i18next.t('cjbusinesstravel-xp-2-chip-6', 'High-Speed Rail'),
        ],
      },
      {
        id: 'food',
        number: '03',
        // 第16次补充需求1：14 → 76（业务方指定，资源仓已发布测试环境）。
        // 按第15次补充确立的通则「改图片的地方不要动文案」，本态 xp-3-* 一系文案与词条一律不动。
        image: img('cjBusinessTravel76.jpg').src,
        imageName: img('cjBusinessTravel76.jpg').imageName,
        name: i18next.t('cjbusinesstravel-xp-3-name', 'Chinese Food'),
        tagline: i18next.t('cjbusinesstravel-xp-3-tagline', 'Regional flavors'),
        mediaLabel: i18next.t('cjbusinesstravel-xp-3-media-label', 'Food Journey'),
        mediaTitle: i18next.t('cjbusinesstravel-xp-3-media-title', 'Taste China, One City at a Time'),
        mediaAlt: i18next.t('cjbusinesstravel-xp-3-media-alt', 'Traditional Chinese food and local dining experience'),
        category: i18next.t('cjbusinesstravel-xp-3-category', 'Chinese Food Journey'),
        title: i18next.t('cjbusinesstravel-xp-3-title', 'Understand China Through Food'),
        text: i18next.t('cjbusinesstravel-xp-3-text', 'Discover the culture of different Chinese regions through local restaurants, traditional dining customs, famous dishes, markets, night streets, and authentic local snacks.'),
        chips: [
          i18next.t('cjbusinesstravel-xp-3-chip-1', 'Guangzhou Morning Tea'),
          i18next.t('cjbusinesstravel-xp-3-chip-2', 'Chengdu Hot Pot'),
          i18next.t('cjbusinesstravel-xp-3-chip-3', 'Xi’an Noodles'),
          i18next.t('cjbusinesstravel-xp-3-chip-4', 'Shanghai Cuisine'),
          i18next.t('cjbusinesstravel-xp-3-chip-5', 'Beijing Roast Duck'),
          i18next.t('cjbusinesstravel-xp-3-chip-6', 'Night Markets'),
        ],
      },
      {
        id: 'local',
        number: '04',
        image: img('cjBusinessTravel15.jpg').src,
        imageName: img('cjBusinessTravel15.jpg').imageName,
        name: i18next.t('cjbusinesstravel-xp-4-name', 'Local Lifestyle'),
        tagline: i18next.t('cjbusinesstravel-xp-4-tagline', 'Everyday China'),
        mediaLabel: i18next.t('cjbusinesstravel-xp-4-media-label', 'Local Lifestyle'),
        mediaTitle: i18next.t('cjbusinesstravel-xp-4-media-title', 'Experience China Like a Local'),
        mediaAlt: i18next.t('cjbusinesstravel-xp-4-media-alt', 'Authentic Chinese local lifestyle and cultural experience'),
        category: i18next.t('cjbusinesstravel-xp-4-category', 'Local Lifestyle'),
        title: i18next.t('cjbusinesstravel-xp-4-title', 'Go Beyond Traditional Sightseeing'),
        text: i18next.t('cjbusinesstravel-xp-4-text', 'Experience a more authentic side of China through neighborhood life, local markets, traditional activities, public spaces, tea culture, and everyday transportation.'),
        chips: [
          i18next.t('cjbusinesstravel-xp-4-chip-1', 'Local Markets'),
          i18next.t('cjbusinesstravel-xp-4-chip-2', 'Traditional Tea Houses'),
          i18next.t('cjbusinesstravel-xp-4-chip-3', 'City Parks'),
          i18next.t('cjbusinesstravel-xp-4-chip-4', 'Old Streets'),
          i18next.t('cjbusinesstravel-xp-4-chip-5', 'Local Communities'),
          i18next.t('cjbusinesstravel-xp-4-chip-6', 'Hanfu Experience'),
        ],
      },
    ],
  };

  // ====================== 区块四之二：China City Vertical Videos（DEV-126262） ======================
  // 需求来源：Plane DEV-126262，提出方直接给了现成模块源码，落盘 docs/旅游落地页/商旅的视频.html。
  // 位置：紧跟区块四 featured-china-experiences 之后（开发当场拍板，工单「仍需确认」第①项）。
  // 图片：源码里是 https://img.youtube.com/vi/<videoId>/hqdefault.jpg 外链，按需求改走公共资源仓
  //       （序号顺延 77~81，对照见 images.js）；**播放地址仍直连 YouTube**，见 components/CityVideos.jsx。
  // 序号 number 与 videoId 是结构性数据、不参与翻译（同 EXPERIENCES 的 number 写法）。
  //
  // DEV-129113（2026-09-15）追加 06/07 两条，原有 01~05 五条**一字未动**：
  // - videoId 取自新版模块源码 test/fixtures/cityVideos260915.html（需求方确认「链接是对的」）；
  // - 文案**不取**该源码里的 06 Video Story / Discover More、07 YouTube Shorts / Explore the Moment
  //   —— 需求方潇潇在企微群「旅游落地页」当场指出这两张文案错了，改用她给的两条；
  // - name 的拼法按需求方口径「按照原有视频的格式来」，即沿用 01~05 的「A, B」两段式逗号写法
  //   （潇潇原文是分两行发的：Tech Hub / Global Trade、Capital / Heritage）。
  // **城市 ↔ 视频的对应按视频实际内容落，不按潇潇群里的发送顺序**（2026-09-15 当日两次拍板的最终口径）：
  //   实测 YouTube 官方标题 XkBV9o-zNJ8 = "This time, we're taking you to Beijing!"、
  //   wFNMLhzsWDk = "Shenzhen moves fast, and so should your itinerary."，故 06=Beijing、07=Shenzhen。
  //   潇潇在群里是先发深圳、后发北京，照发送顺序落就会「点开 Shenzhen 卡播的是北京片子」；
  //   开发把上述实测证据摆出来后，需求方当日先按发送顺序拍了一次，看到证据后改判为按视频内容对应。
  //   **封面图不随城市名对调**：82/83 分别是 XkBV9o-zNJ8 / wFNMLhzsWDk 自己的缩略图，跟着 videoId
  //   走而不是跟着城市名走 —— 城市名换过来之后，缩略图与视频内容本来就是对上的。
  var CITY_VIDEOS = {
    items: [
      {
        id: 'hangzhou',
        number: '01',
        videoId: 'qR5TK4SjRdg',
        image: img('cjBusinessTravel77.jpg').src,
        imageName: img('cjBusinessTravel77.jpg').imageName,
        alt: i18next.t('cjbusinesstravel-video-1-alt', 'Hangzhou China'),
        ariaLabel: i18next.t('cjbusinesstravel-video-1-aria', 'Watch Hangzhou video'),
        location: i18next.t('cjbusinesstravel-video-1-location', 'Hangzhou · China'),
        name: i18next.t('cjbusinesstravel-video-1-name', 'West Lake & Timeless Charm'),
      },
      {
        id: 'chengdu',
        number: '02',
        videoId: '2yk636WLnd0',
        image: img('cjBusinessTravel78.jpg').src,
        imageName: img('cjBusinessTravel78.jpg').imageName,
        alt: i18next.t('cjbusinesstravel-video-2-alt', 'Chengdu China'),
        ariaLabel: i18next.t('cjbusinesstravel-video-2-aria', 'Watch Chengdu video'),
        location: i18next.t('cjbusinesstravel-video-2-location', 'Chengdu · China'),
        name: i18next.t('cjbusinesstravel-video-2-name', 'Pandas, Flavor & Slow Living'),
      },
      {
        id: 'xian',
        number: '03',
        videoId: 'nztG5dYjElA',
        image: img('cjBusinessTravel79.jpg').src,
        imageName: img('cjBusinessTravel79.jpg').imageName,
        // 本模块源码里 Xi'an 用的是直角撇号（与全页其它区块的 U+2019 弯引号不同），
        // 按「英文源即原型原文」的既定口径照抄，不擅自统一 —— 改了就与需求给的源码对不上。
        alt: i18next.t('cjbusinesstravel-video-3-alt', 'Xi\'an China'),
        ariaLabel: i18next.t('cjbusinesstravel-video-3-aria', 'Watch Xi\'an video'),
        location: i18next.t('cjbusinesstravel-video-3-location', 'Xi\'an · China'),
        name: i18next.t('cjbusinesstravel-video-3-name', 'Ancient Capital, Living History'),
      },
      {
        id: 'guangzhou',
        number: '04',
        videoId: 'NgjMvYwfxs4',
        image: img('cjBusinessTravel80.jpg').src,
        imageName: img('cjBusinessTravel80.jpg').imageName,
        alt: i18next.t('cjbusinesstravel-video-4-alt', 'Guangzhou China'),
        ariaLabel: i18next.t('cjbusinesstravel-video-4-aria', 'Watch Guangzhou video'),
        location: i18next.t('cjbusinesstravel-video-4-location', 'Guangzhou · China'),
        name: i18next.t('cjbusinesstravel-video-4-name', 'Canton Culture, Modern Energy'),
      },
      {
        id: 'chongqing',
        number: '05',
        videoId: 'watliNVeMvM',
        image: img('cjBusinessTravel81.jpg').src,
        imageName: img('cjBusinessTravel81.jpg').imageName,
        alt: i18next.t('cjbusinesstravel-video-5-alt', 'Chongqing China'),
        ariaLabel: i18next.t('cjbusinesstravel-video-5-aria', 'Watch Chongqing video'),
        location: i18next.t('cjbusinesstravel-video-5-location', 'Chongqing · China'),
        name: i18next.t('cjbusinesstravel-video-5-name', 'Mountain City, Endless Layers'),
      },
      {
        id: 'beijing',
        number: '06',
        videoId: 'XkBV9o-zNJ8',
        image: img('cjBusinessTravel82.jpg').src,
        imageName: img('cjBusinessTravel82.jpg').imageName,
        alt: i18next.t('cjbusinesstravel-video-6-alt', 'Beijing China'),
        ariaLabel: i18next.t('cjbusinesstravel-video-6-aria', 'Watch Beijing video'),
        location: i18next.t('cjbusinesstravel-video-6-location', 'Beijing · China'),
        name: i18next.t('cjbusinesstravel-video-6-name', 'Capital, Heritage'),
      },
      {
        id: 'shenzhen',
        number: '07',
        videoId: 'wFNMLhzsWDk',
        image: img('cjBusinessTravel83.jpg').src,
        imageName: img('cjBusinessTravel83.jpg').imageName,
        alt: i18next.t('cjbusinesstravel-video-7-alt', 'Shenzhen China'),
        ariaLabel: i18next.t('cjbusinesstravel-video-7-aria', 'Watch Shenzhen video'),
        location: i18next.t('cjbusinesstravel-video-7-location', 'Shenzhen · China'),
        name: i18next.t('cjbusinesstravel-video-7-name', 'Tech Hub, Global Trade'),
      },
    ],
    // DEV-129113 轮播控件的无障碍文案，逐字取自新版源码的 aria-label。
    // 页码点源码是 "Go to slide " + (i + 1)，序号在组件里拼；key 仍是字面量，
    // 否则 @cckj/i18n-util 的文本扫描上报不到（CASE-L4/CASE-L5 的既有教训）。
    a11y: {
      prev: i18next.t('cjbusinesstravel-video-prev', 'Previous video'),
      next: i18next.t('cjbusinesstravel-video-next', 'Next video'),
      goto: i18next.t('cjbusinesstravel-video-goto', 'Go to slide'),
    },
  };

  // ============================== 区块五：Our Travel Services + Destinations ==============================
  // 原型 .cjbt-luxury-services
  var SERVICES = {
    eyebrow: i18next.t('cjbusinesstravel-svc-eyebrow', 'Our Travel Services'),
    titleLead: i18next.t('cjbusinesstravel-svc-title-lead', 'Travel China with'),
    titleEm: i18next.t('cjbusinesstravel-svc-title-em', 'Comfort and Confidence'),
    description: i18next.t('cjbusinesstravel-svc-description', 'From transportation and accommodation to sightseeing and itinerary coordination, our local team helps manage the details of your journey across China.'),
    cards: [
      {
        icon: 'transport',
        number: '01',
        title: i18next.t('cjbusinesstravel-svc-1-title', 'Transportation Across China'),
        intro: i18next.t('cjbusinesstravel-svc-1-intro', 'Travel smoothly between cities with coordinated transportation arrangements based on your confirmed route.'),
        list: [
          i18next.t('cjbusinesstravel-svc-1-item-1', 'Domestic high-speed rail arrangements based on your route'),
          i18next.t('cjbusinesstravel-svc-1-item-2', 'Domestic flight arrangements based on distance and itinerary'),
          i18next.t('cjbusinesstravel-svc-1-item-3', 'Practical transportation route planning between cities'),
          i18next.t('cjbusinesstravel-svc-1-item-4', 'Coordination of connections between each destination'),
          i18next.t('cjbusinesstravel-svc-1-item-5', 'Less time spent booking tickets and planning routes yourself'),
        ],
        notice: i18next.t('cjbusinesstravel-svc-1-notice', 'Transportation standards and included services are subject to the final confirmed itinerary.'),
      },
      {
        icon: 'hotel',
        number: '02',
        title: i18next.t('cjbusinesstravel-svc-2-title', 'Five-Star Hotel Accommodation'),
        intro: i18next.t('cjbusinesstravel-svc-2-intro', 'Enjoy comfortable, secure, and high-quality hotel stays throughout your China journey.'),
        list: [
          i18next.t('cjbusinesstravel-svc-2-item-1', 'Five-star hotel arrangements in each confirmed city'),
          i18next.t('cjbusinesstravel-svc-2-item-2', 'Convenient hotel locations based on the travel schedule'),
          i18next.t('cjbusinesstravel-svc-2-item-3', 'Comfortable, safe, and high-quality accommodation'),
          i18next.t('cjbusinesstravel-svc-2-item-4', 'Easy access to CJEC, business activities, and sightseeing'),
          i18next.t('cjbusinesstravel-svc-2-item-5', 'Hotel arrangements coordinated with city transportation'),
        ],
        notice: i18next.t('cjbusinesstravel-svc-2-notice', 'Hotel selections and the number of accommodation nights are subject to the final itinerary confirmation.'),
      },
      {
        icon: 'sightseeing',
        number: '03',
        title: i18next.t('cjbusinesstravel-svc-3-title', 'All-Inclusive Sightseeing Option'),
        intro: i18next.t('cjbusinesstravel-svc-3-intro', 'Select a comprehensive sightseeing plan and let our local team coordinate the details of your travel experience.'),
        list: [
          i18next.t('cjbusinesstravel-svc-3-item-1', 'Visits to major attractions in each selected city'),
          i18next.t('cjbusinesstravel-svc-3-item-2', 'English-speaking explanations at cultural attractions'),
          i18next.t('cjbusinesstravel-svc-3-item-3', 'Local cultural and lifestyle experiences'),
          i18next.t('cjbusinesstravel-svc-3-item-4', 'Attraction ticket arrangements'),
          i18next.t('cjbusinesstravel-svc-3-item-5', 'City transportation and itinerary transfers'),
          i18next.t('cjbusinesstravel-svc-3-item-6', 'Professional planning and time management'),
          i18next.t('cjbusinesstravel-svc-3-item-7', 'Recommended sightseeing routes'),
          i18next.t('cjbusinesstravel-svc-3-item-8', 'Coordination between travel and business activities'),
        ],
        notice: i18next.t('cjbusinesstravel-svc-3-notice', 'Included sightseeing services depend on the final confirmed travel package.'),
      },
    ],
    destinationTitle: i18next.t('cjbusinesstravel-svc-destination-title', 'Available China Destinations'),
    destinationText: i18next.t('cjbusinesstravel-svc-destination-text', 'Select one destination for a deeper experience or combine several cities into a personalized multi-city journey.'),
    // 第15次补充需求2：删 Beijing(8) / Suzhou(10) / Nanjing(11) / Changsha(12) / Jiaxin(13) / Wuhan(14)，
    // 由 14 城收敛为 8 城。**剩余 key 一律保留原编号、不重排**（故 8 与 10~14 成为空号）——
    // 翻译中心按 key 存译文，重排会让 Shanghai 拿到原 svc-city-8(Beijing) 的译文，
    // 属于只在多语言站点才暴露的隐性错译。空号由 CASE-C8 锁住城市清单本身，不靠编号连续性保证。
    cities: [
      i18next.t('cjbusinesstravel-svc-city-1', 'Guangzhou'),
      i18next.t('cjbusinesstravel-svc-city-2', 'Shenzhen'),
      i18next.t('cjbusinesstravel-svc-city-3', 'Zhangjiajie'),
      i18next.t('cjbusinesstravel-svc-city-4', 'Hangzhou'),
      i18next.t('cjbusinesstravel-svc-city-5', 'Chongqing'),
      i18next.t('cjbusinesstravel-svc-city-6', 'Chengdu'),
      i18next.t('cjbusinesstravel-svc-city-7', 'Xi’an'),
      i18next.t('cjbusinesstravel-svc-city-9', 'Shanghai'),
    ],
    // 第15次补充需求2：'Other China Cities' → 'Other Cities'。同需求1 的道理，原 key
    // cjbusinesstravel-svc-city-more 已按 'Other China Cities' 译完各语种，改值不会重新翻译；
    // 支付区块的 cjbusinesstravel-pay-city-12 值本就是 'Other Cities' 且已译好，直接复用它，
    // 原 svc-city-more 词条删除。两处兜底 chip 共用一条词条，措辞与译文天然一致。
    cityMore: i18next.t('cjbusinesstravel-pay-city-12', 'Other Cities'),
  };

  // ============================== 区块六：Price & Payment + CJ Points + Core Values ==============================
  // 原型 .cjbt-premium-pay：左支付面板 + 右套餐详情
  var PAYMENT = {
    name: i18next.t('cjbusinesstravel-pay-name', 'Customized China Journey'),
    priceLabel: i18next.t('cjbusinesstravel-pay-price-label', 'Reserve Your Journey'),
    currency: '$',
    amount: '999',
    deposit: i18next.t('cjbusinesstravel-pay-deposit', 'Initial Reservation Deposit'),
    priceDescription: i18next.t('cjbusinesstravel-pay-price-description', 'Secure your customized China journey with an initial deposit. The final price is confirmed after your cities, travel days, transportation, hotel arrangements, sightseeing experiences, and optional business services have been selected.'),
    quickItems: [
      { title: i18next.t('cjbusinesstravel-pay-quick-1-title', 'Custom Itinerary'), text: i18next.t('cjbusinesstravel-pay-quick-1-text', 'Single-city or multi-city travel planning') },
      { title: i18next.t('cjbusinesstravel-pay-quick-2-title', 'Flexible Schedule'), text: i18next.t('cjbusinesstravel-pay-quick-2-text', 'Travel dates and city stays designed around you') },
      { title: i18next.t('cjbusinesstravel-pay-quick-3-title', 'Premium Support'), text: i18next.t('cjbusinesstravel-pay-quick-3-text', 'Professional English-speaking travel assistance') },
      { title: i18next.t('cjbusinesstravel-pay-quick-4-title', 'Business Access'), text: i18next.t('cjbusinesstravel-pay-quick-4-text', 'Optional CJEC, Canton Fair, factory, and supplier visits') },
    ],
    noticeStrong: i18next.t('cjbusinesstravel-pay-notice-strong', 'Before you reserve:'),
    noticeText: i18next.t('cjbusinesstravel-pay-notice-text', 'USD 999 is the initial reservation deposit and is not the final package price. The deposit deduction, cancellation, refund, and rescheduling terms must be confirmed before payment.'),
    primaryCta: i18next.t('cjbusinesstravel-pay-cta-primary', 'Reserve with USD 999 Deposit'),
    primaryCtaHref: LINK_PAYMENT,
    secondaryCta: i18next.t('cjbusinesstravel-pay-cta-secondary', 'Talk to a Travel Advisor'),
    secondaryCtaHref: LINK_WHATSAPP,
    detailsEyebrow: i18next.t('cjbusinesstravel-pay-details-eyebrow', 'Complete China Travel Package'),
    detailsTitleLead: i18next.t('cjbusinesstravel-pay-details-title-lead', 'Business, Culture and Travel'),
    detailsTitleEm: i18next.t('cjbusinesstravel-pay-details-title-em', 'in One Journey'),
    detailsDescription: i18next.t('cjbusinesstravel-pay-details-description', 'Combine the Canton Fair, CJEC Guangzhou, supplier meetings, factory visits, supply chain discovery, city exploration, cultural experiences, and premium travel support in one coordinated journey across China.'),
    pointsIcon: i18next.t('cjbusinesstravel-pay-points-icon', 'CJ'),
    pointsBadge: i18next.t('cjbusinesstravel-pay-points-badge', 'CJ Member Exclusive'),
    pointsTitle: i18next.t('cjbusinesstravel-pay-points-title', 'Use CJ Points to Reduce Your Travel Cost'),
    pointsText: i18next.t('cjbusinesstravel-pay-points-text', 'Eligible CJdropshipping users can apply qualifying CJ Points toward selected travel service fees and reduce the payable package cost.'),
    pointsCta: i18next.t('cjbusinesstravel-pay-points-cta', 'Check & Use CJ Points'),
    pointsCtaHref: LINK_POINTS,
    values: [
      { title: i18next.t('cjbusinesstravel-pay-value-1-title', 'Business Connections'), text: i18next.t('cjbusinesstravel-pay-value-1-text', 'CJEC, suppliers, factories, industrial clusters, and business resources.') },
      { title: i18next.t('cjbusinesstravel-pay-value-2-title', 'Premium Travel'), text: i18next.t('cjbusinesstravel-pay-value-2-text', 'Hotels, transportation, sightseeing, and local English-speaking support.') },
      { title: i18next.t('cjbusinesstravel-pay-value-3-title', 'Less Planning'), text: i18next.t('cjbusinesstravel-pay-value-3-text', 'One coordinated itinerary covering multiple Chinese cities and activities.') },
    ],
    includesTitle: i18next.t('cjbusinesstravel-pay-includes-title', 'Package Includes'),
    includes: [
      i18next.t('cjbusinesstravel-pay-include-1', 'One complimentary CJEC Guangzhou 2026 summit ticket'),
      i18next.t('cjbusinesstravel-pay-include-2', 'Professional English-speaking guide throughout the journey'),
      i18next.t('cjbusinesstravel-pay-include-3', 'Customized single-city or multi-city China itinerary'),
      i18next.t('cjbusinesstravel-pay-include-4', 'Domestic high-speed rail or flight coordination'),
      i18next.t('cjbusinesstravel-pay-include-5', 'Five-star hotel accommodation in confirmed itinerary cities'),
      i18next.t('cjbusinesstravel-pay-include-6', 'Standard itinerary or fully inclusive sightseeing option'),
      i18next.t('cjbusinesstravel-pay-include-7', 'Optional Canton Fair, factory, supplier, and CJ visits'),
      i18next.t('cjbusinesstravel-pay-include-8', 'Business activities, city travel, and cultural experiences combined'),
    ],
    citiesTitle: i18next.t('cjbusinesstravel-pay-cities-title', 'Available China Destinations'),
    // 第15次补充需求3：删 Beijing(8) / Suzhou(10) / Nanjing(11)，与需求2 的 Services 8 城口径一致
    // （本区块少删 Changsha/Jiaxin/Wuhan 是因为原本就没有这三城）。末位 city-12 是兜底项「Other Cities」，
    // 保留不动。同 SERVICES：剩余 key 保留原编号不重排，避免翻译中心按 key 存的译文错位。
    cities: [
      i18next.t('cjbusinesstravel-pay-city-1', 'Guangzhou'),
      i18next.t('cjbusinesstravel-pay-city-2', 'Shenzhen'),
      i18next.t('cjbusinesstravel-pay-city-3', 'Zhangjiajie'),
      i18next.t('cjbusinesstravel-pay-city-4', 'Hangzhou'),
      i18next.t('cjbusinesstravel-pay-city-5', 'Chongqing'),
      i18next.t('cjbusinesstravel-pay-city-6', 'Chengdu'),
      i18next.t('cjbusinesstravel-pay-city-7', 'Xi’an'),
      i18next.t('cjbusinesstravel-pay-city-9', 'Shanghai'),
      i18next.t('cjbusinesstravel-pay-city-12', 'Other Cities'),
    ],
  };

  // ============================== 区块七：City Explorer（八城） ==============================
  // 原型 .cjbt-city-five：城市导航 tab + 城市信息面板 + 图库轮播（含拖拽）
  var CITIES = {
    eyebrow: i18next.t('cjbusinesstravel-cityx-eyebrow', 'Featured China Destinations'),
    titleLead: i18next.t('cjbusinesstravel-cityx-title-lead', 'Discover China'),
    titleEm: i18next.t('cjbusinesstravel-cityx-title-em', 'City by City'),
    headingText: i18next.t('cjbusinesstravel-cityx-heading-text', 'Select a destination to explore its culture, food, landmarks, lifestyle, and optional business opportunities. Each city can be added to a personalized single-city or multi-city China journey.'),
    navLabel: i18next.t('cjbusinesstravel-cityx-nav-label', 'China destinations'),
    galleryTitle: i18next.t('cjbusinesstravel-cityx-gallery-title', 'Discover the City Through Every Frame'),
    // 图库标签形如「Guangzhou Gallery」，城市名动态拼接（原型为 city.name + " Gallery"）
    gallerySuffix: i18next.t('cjbusinesstravel-cityx-gallery-suffix', 'Gallery'),
    prevLabel: i18next.t('cjbusinesstravel-cityx-prev-label', 'Previous images'),
    nextLabel: i18next.t('cjbusinesstravel-cityx-next-label', 'Next images'),
    interactionNote: i18next.t('cjbusinesstravel-cityx-interaction-note', 'Click the arrows, select a navigation point, or drag the gallery.'),
    // 城市信息卡左上角标签，原型为 `01 · Featured Destination`（序号由渲染侧拼接）
    featuredLabel: i18next.t('cjbusinesstravel-cityx-featured-label', 'Featured Destination'),
    // 下列两项文案随城市/序号变化，用插值函数返回（非字符串叶子，不参与 CASE-C2 的 {{}} 扫描）。
    // 取词写在这里、key 写成字面量，是为了能被翻译上报扫到——原先封进 dyn() 由变量取词，
    // 导致这两条从未上报（详见 fillVars 注释与 CASE-L4）。
    addToJourney: function (cityName) {
      return fillVars(
        __ready ? i18next.t('cjbusinesstravel-cityx-add-to-journey', { defaultValue: 'Add {{city}} to My Journey' }) : '',
        'Add {{city}} to My Journey',
        { city: cityName }
      );
    },
    addToJourneyHref: LINK_WHATSAPP,
    dotLabel: function (position) {
      return fillVars(
        __ready ? i18next.t('cjbusinesstravel-cityx-dot-label', { defaultValue: 'View gallery position {{position}}' }) : '',
        'View gallery position {{position}}',
        { position: position }
      );
    },
    list: getCities(cdnHost),
  };

  // ============================== 区块八：Plan Your Journey（底部 CTA） ==============================
  // 原型 .cjbt-compact-cta
  var CTA = {
    eyebrow: i18next.t('cjbusinesstravel-cta-eyebrow', 'CJ Business Travel'),
    titleLead: i18next.t('cjbusinesstravel-cta-title-lead', 'Ready to Experience'),
    titleEm: i18next.t('cjbusinesstravel-cta-title-em', 'the Real China?'),
    description: i18next.t('cjbusinesstravel-cta-description', 'Explore inspiring Chinese cities, experience authentic culture and local life, and add business visits or supply chain opportunities based on your travel goals.'),
    benefits: [
      i18next.t('cjbusinesstravel-cta-benefit-1', 'Customized Travel Itinerary'),
      i18next.t('cjbusinesstravel-cta-benefit-2', 'English-Speaking Support'),
      i18next.t('cjbusinesstravel-cta-benefit-3', 'Travel, Culture and Business'),
    ],
    primaryCta: i18next.t('cjbusinesstravel-cta-primary', 'Plan My China Journey'),
    // 原型 href="#cjbt-contact" 断链，曾改跳 WhatsApp；第14次补充改跳「价格与支付」区块。
    // 站内锚点不能带 target="_blank"，Cta.jsx 已按 href 形态条件渲染（副按钮仍是外链）。
    primaryCtaHref: '#cjbt-package-payment',
    secondaryCta: i18next.t('cjbusinesstravel-cta-secondary', 'Contact Travel Advisor'),
    secondaryCtaHref: LINK_WHATSAPP,
    // 第2次补充：CTA 区块 CSS 背景图，同 Hero 走 CSS 变量注入
    backgroundCss: 'url(' + imgUrl(cdnHost, 'cjBusinessTravel01.jpg') + ')',
    cardLabel: i18next.t('cjbusinesstravel-cta-card-label', 'Start Your Journey'),
    cardTitle: i18next.t('cjbusinesstravel-cta-card-title', 'Plan Your China Trip in Three Steps'),
    // 需求来源：AI提示词.md 第1次提交第1次补充（DEV-120352）—— 三步骤的序号位改用 iconfont 图标：
    //   Share Your Plan=(iconjihua 计划) / Receive Your Itinerary=(iconhangchengdan 行程单) /
    //   Confirm and Travel=(iconlvhang 旅行)。三个码位由 iconfont 项目 1265799 新版 fdkj2fnqx4b 提供
    //   （见 app/web/layout/index.jsx 的升版说明）。字形字符直接落数据，渲染侧套 .iconfont 字体族。
    steps: [
      { number: '01', icon: '\ue8f1', title: i18next.t('cjbusinesstravel-cta-step-1-title', 'Share Your Plan'), text: i18next.t('cjbusinesstravel-cta-step-1-text', 'Tell us your dates, cities, interests, and budget.') },
      { number: '02', icon: '\ue8f4', title: i18next.t('cjbusinesstravel-cta-step-2-title', 'Receive Your Itinerary'), text: i18next.t('cjbusinesstravel-cta-step-2-text', 'Our local team creates a personalized route.') },
      { number: '03', icon: '\ue8f2', title: i18next.t('cjbusinesstravel-cta-step-3-title', 'Confirm and Travel'), text: i18next.t('cjbusinesstravel-cta-step-3-text', 'Finalize your plan and explore China with local support.') },
    ],
  };

  return {
    HERO: HERO,
    ABOUT: ABOUT,
    TRIP: TRIP,
    EXPERIENCES: EXPERIENCES,
    CITY_VIDEOS: CITY_VIDEOS,
    SERVICES: SERVICES,
    PAYMENT: PAYMENT,
    CITIES: CITIES,
    CTA: CTA,
  };
}

/**
 * 给 Hero 轨道图片附加城市标注与 alt/loading（原型 .cjbt-v4__image-card 结构）。
 * @param {{imageName: string, src: *}} item images.img() 产物
 * @param {string} city 卡片右下角城市标注
 * @param {string} alt 图片 alt
 * @param {string} loading 原型的 loading 策略（eager/lazy）
 */
function mergeCaption(item, city, alt, loading) {
  item.city = city;
  item.alt = alt;
  item.loading = loading;
  return item;
}

module.exports = { getConstants: getConstants };
